<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import DataTable from "./DataTable.vue";
import Modal from "./Modal.vue";
import Button from "./Button.vue";
import services from "@/services";
import { useAuthStore } from "@/stores/authStore";
import { useToaster } from "ram-components";

type ModalMode = "create" | "edit";

const showUserModal = ref(false);
const modalMode = ref<ModalMode>("create");
const selectedUser = ref<any | null>(null);
const isLoading = ref(false);
let CurrentUserID = ref(0);

const authStore = useAuthStore();
const toaster = useToaster();

const first_name = ref("");
const last_name = ref("");
const email = ref("");
const contact_no = ref("");
const profile = ref("");
const isActive = ref(true);
const saving = ref(false);

const columns = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "profile", label: "Profile" },
  { key: "status", label: "Status" },
  { key: "action", label: "Action" },
];

const users = ref<any[]>([]);

async function loadUsers() {
  isLoading.value = true;
  try {
    const response = await services.getUserDetails();
    const result = response.data.result || [];
    users.value = result.map((u: any) => ({
      id: u.recordID,
      name: u.name,
      email: u.email,
      profile: u.userProfile,
      status: u.status,
    }));
  } catch (error) {
    toaster.push({
      type: "error",
      text: "Failed to load users",
    });
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  loadUsers();
});

const isSelfEditing = computed(() => {
  if (modalMode.value === "edit" && selectedUser.value) {
    return selectedUser.value.id === authStore.currentUserID;
  }
  return false;
});

const isActivationPending = computed(() => {
  return selectedUser.value?.status?.replace(/\s/g, "").toLowerCase() === "pending";
});

async function openEditModal(row: any) {
  modalMode.value = "edit";
  selectedUser.value = row;
  resetForm();

  try {
    const response = await services.getUserById(row.id);

    const result = response.data.result;
    first_name.value = result.firstName;
    last_name.value = result.lastName;
    email.value = result.emailAddress;
    contact_no.value = result.contactNumber;
    profile.value = result.userProfile;
    isActive.value = result.isActive;

    showUserModal.value = true;
  } catch (error) {
    toaster.push({
      type: "error",
      text: "Failed to fetch User details",
    });
  }
}
const isEdit = modalMode.value === "edit";
async function submitUser() {
  saving.value = true;
  if (!isvalid()) return;
  let isActiveFlag: any = isActive.value ? true : false;
  if (isActivationPending.value) {
    isActiveFlag = null;
  }
  const payload = {
    recordID:
      modalMode.value === "edit" && selectedUser.value
        ? selectedUser.value.id
        : 0,
    firstName: first_name.value,
    lastName: last_name.value,
    emailID: email.value,
    contactNumber: String(contact_no.value),
    userProfile: profile.value,
    isActive: isActiveFlag,
    createdBy: authStore.currentUserID,
    ...(modalMode.value === "edit" && { updatedBy: authStore.currentUserID }),
  };

  try {
    if (modalMode.value === "create") {
      await services.saveUser(payload);
      toaster.push({
        type: "success",
        text: "User Created Successfully!",
      });
    } else {
      await services.updateUser(payload);
      toaster.push({
        type: "success",
        text: "User Updated Successfully!",
      });
    }
    closeUserModal();
    await loadUsers();
  } catch (error: any) {
    showUserModal.value = false;
    toaster.push({
      type: "error",
      text: error.response?.data?.message || "Failed to save user",
    });
  } finally {
    saving.value = false;
  }
}

function resetForm() {
  first_name.value = "";
  last_name.value = "";
  email.value = "";
  contact_no.value = "";
  profile.value = "";
  isActive.value = true;
}

function openCreateModal() {
  modalMode.value = "create";
  CurrentUserID = selectedUser.value;
  selectedUser.value = null;
  resetForm();
  showUserModal.value = true;
}

function getColor(data: any) {
  const status = data.status.replace(/\s/g, "").toLowerCase();

  if (status === "active") {
    return "bg-green-100 text-green-700";
  } else if (status === "inactive") {
    return "bg-red-100 text-red-700";
  } else {
    return "bg-yellow-100 text-yellow-700";
  }
}

function closeUserModal() {
  showUserModal.value = false;
}

function isvalid() {
  return (
    first_name.value &&
    last_name.value &&
    email.value &&
    contact_no.value &&
    profile.value
  );
}
</script>
<template>
  <div class="rounded-xl border bg-white">
    <div class="flex items-center justify-between border-b p-4">
      <h2 class="font-semibold text-gray-900">User Management</h2>
      <button
        @click="openCreateModal"
        class="rounded-lg bg-ram-red px-4 py-2 text-sm text-white"
      >
        Add User
      </button>
    </div>

    <DataTable :columns="columns" :data="users" :loading="isLoading">
      <template #cell-status="{ row }">
        <span
          :class="[getColor(row)]"
          class="rounded-lg p-1 text-center text-xs"
        >
          {{ row.status }}
        </span>
      </template>

      <template #cell-action="{ row }">
        <button
          class="text-sm text-blue-600 hover:underline"
          @click="openEditModal(row)"
        >
          Edit
        </button>
      </template>
    </DataTable>
  </div>

  <Modal
    :is-open="showUserModal"
    :title="modalMode === 'create' ? 'Add User' : 'Edit User'"
    size="lg"
    @close="closeUserModal"
  >
    <FormKit
      type="form"
      :actions="false"
      @submit="submitUser"
      :incomplete-message="false"
    >
      <div class="space-y-4">
        <div class="flex gap-2">
          <FormKit
            v-model="first_name"
            label="First Name"
            input-class="w-full px-2 py-1 my-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            label-class=" text-sm font-medium text-gray-700 block"
          />
          <FormKit
            v-model="last_name"
            label="Last Name"
            input-class="w-full px-2 py-1 my-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            label-class=" text-sm font-medium text-gray-700 block"
          />
        </div>

        <FormKit
          v-model="email"
          label="Email"
          :disabled="modalMode === 'edit'"
          type="email"
          input-class="w-full px-2 py-1 my-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          label-class=" text-sm font-medium text-gray-700 block"
        />

        <FormKit
          v-model="contact_no"
          label="Contact Number"
          v-limit="10"
          type="tel"
          validation="required|matches:/^[0-9]{10}$/"
          :validation-messages="{
            matches: 'Contact number must be exactly 10 digits',
            required: 'Contact number is required',
          }"
          input-class="w-full px-2 py-1 my-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          label-class=" text-sm font-medium text-gray-700 block"
          messages-class="text-sm text-red-500 mt-1"
        />

        <FormKit
          type="select"
          label="Profile"
          v-model="profile"
          placeholder="Select User Profile"
          :options="['Processing Clerk', 'Full Access']"
          options-class="text-sm"
          input-class="w-full px-2 py-1 my-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          label-class=" text-sm font-medium text-gray-700 block"
        />

        <FormKit
          v-if="modalMode === 'edit'"
          type="checkbox"
          v-model="isActive"
          :disabled="isSelfEditing || isActivationPending"
  label="User Active"
  :help="
    isSelfEditing 
      ? 'You cannot deactivate your own account.' 
      : isActivationPending 
      ? 'Status cannot be changed while activation is pending.' 
      : ''
  "
          :classes="{
            input:
              'h-4 w-4 mr-2 rounded border-gray-900 text-ram-red focus:ring-ram-red disabled:bg-gray-300',
            label: 'text-sm font-medium text-gray-700 cursor-pointer',
            help: 'text-xs text-orange-600 mt-1',
          }"
        />
      </div>

      <div class="mt-4 flex justify-end gap-3">
        <Button variant="secondary" @click="closeUserModal"> Cancel </Button>
        <Button variant="ram" type="submit" :disabled="!isvalid() || saving">
          {{ saving ? "Saving..." : "Save User" }}
        </Button>
      </div>
    </FormKit>
  </Modal>
</template>
